import {Stack, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {AppointmentBookingEnum, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {BookingNotFoundVariantEnum} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundDialog/types';
import {BookingNotFoundListCard} from 'presentation/modules/booking/shared/ui/components/BookingNotFoundListCard';
import {MostSearchedList} from 'presentation/modules/booking/shared/ui/components/MostSearchedList';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';
import {translations} from 'presentation/translations-new';
import {formatText, isSelectedSpecialty} from 'presentation/utils';
import React, {Fragment} from 'react';

import {StyledTypography} from './styles';
import {TelehealthSpecialtyListProps} from './types';
import {AlertModal} from 'presentation/components';
import {TherapyAlert} from 'presentation/modules/booking/shared/ui/components/TherapyAlert';
import {BeBornWellDrawer} from 'presentation/modules/booking/shared/ui/components/BeBornWellDrawer';
import {BeBornWellCard} from 'presentation/modules/booking/shared/ui/components/BeBornWellCard';
import {BeBornWellNotRegister} from 'presentation/modules/booking/shared/ui/components/BeBornWellNotRegister';

import {ScriptModal} from 'presentation/modules/booking/shared/ui/components/ScriptModal';
import {useTelehealthSpecialtiesSelection} from './hooks/useTelehealthSpecialtiesSelection';
import {CheckBookingModalEnum} from './hooks/useTelehealthSpecialtiesSelection/types';
import {useBeBornWell} from 'presentation/modules/booking/shared/hooks/useBeBornWell';
import {TelehealthDuplicityDialog} from '../TelehealthDuplicityDialog';
import {TeleHealthCard} from './components/TeleHealthCard';

const {resultFound, serverError, serverErrorTitle, tryAgain, unexpectedError} =
	translations['pt-br'].commons;
const {notFoundModal} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

export function TelehealthSpecialtiesList({
	handleNextStep,
	specialtiesList,
	mostSearchedSpecialties,
}: TelehealthSpecialtyListProps) {
	const {
		query,
		handleSelectCard,
		checkBookingModal,
		setCheckBookingModal,
		specialtyCode,
		subSpecialtyCode,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		scheduledBooking,
		handleCheckTelehealthDuplicity,
	} = useTelehealthSpecialtiesSelection({
		handleNextStep,
	});

	const {
		showBeBornWellCard,
		showBeBornWellNotRegister,
		openBeBornWellDrawer,
		setOpenBeBornWellDrawer,
		handleNotPregnantClick,
		handleShowBeBornWellCard,
		handleShowBeBornWellNotRegister,
		handleContinueToBooking,
		selectedSpecialty,
		handleSelectCardWrapper,
	} = useBeBornWell({
		handleCheckDuplicity: handleCheckTelehealthDuplicity,
		handleSelectCard,
		setCheckBookingModal,
	});

	return (
		<Fragment>
			{openBeBornWellDrawer ? (
				<BeBornWellDrawer
					open={openBeBornWellDrawer}
					setOpen={setOpenBeBornWellDrawer}
					handleNotPregnant={handleNotPregnantClick}
					handleContinueToBooking={handleContinueToBooking}
					onShowBeBornWellCard={handleShowBeBornWellCard}
					onShowBeBornWellNotRegister={handleShowBeBornWellNotRegister}
				/>
			) : null}

			{showBeBornWellCard ? (
				<BeBornWellCard continueToBooking={handleContinueToBooking} />
			) : null}

			{showBeBornWellNotRegister ? (
				<BeBornWellNotRegister
					selectedSpecialty={selectedSpecialty || undefined}
					handleSelectCard={handleSelectCard}
					checkBookingModal={checkBookingModal}
					handleCheckDuplicity={handleCheckTelehealthDuplicity}
				/>
			) : null}

			{!showBeBornWellCard && !showBeBornWellNotRegister ? (
				<Fragment>
					{query ? (
						<Typography variant="subtitle2" color="text.secondary">
							{resultFound}
						</Typography>
					) : null}
					<MostSearchedList
						flowType={FlowEnum.TELEHEALTH}
						handleSelectCard={handleSelectCardWrapper}
						mostSearchedArr={mostSearchedSpecialties}
					/>

					{specialtiesList?.map(categoryItem =>
						categoryItem?.list?.length === 0 ? null : (
							<Stack key={categoryItem?.category} mt={2} gap={2}>
								<StyledTypography variant="h6" id={categoryItem?.category}>
									{categoryItem?.category}
								</StyledTypography>
								<Grid container rowSpacing={2} columnSpacing={3}>
									{categoryItem?.list.map((item, index) => (
										<Grid key={index} size={{xs: 12, lg: 6}}>
											<TeleHealthCard
												dataTagId={
													AppointmentBookingEnum.SELECIONOU_ESPECIALIDADE_TEXT
												}
												text={formatText(item?.formattedText)}
												handleClick={() => handleSelectCardWrapper(item)}
												selected={isSelectedSpecialty(
													item,
													specialtyCode,
													subSpecialtyCode,
												)}
												showSpecialtyDetailsButton={item.hasDefinition}
												specialtyCode={item.code}
											/>
										</Grid>
									))}
								</Grid>
							</Stack>
						),
					)}

					<Stack mt={2}>
						<BookingNotFoundListCard
							openModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.TELEHEALTH}`}
							modalHeaderModalDataTagId={`${OdontoBookingTaggingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.TELEHEALTH}`}
							modalCloseModalDataTagId={`${OdontoBookingTaggingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_ESPECIALIDADE_DESEJADA}${FlowEnum.TELEHEALTH}`}
							modalFirstActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO}${FlowEnum.TELEHEALTH}`}
							modalSecondActionDataTagId={`${OdontoBookingTaggingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE}${FlowEnum.TELEHEALTH}`}
							flowType={FlowEnum.TELEHEALTH}
							customOrientationText={notFoundModal.cardListText}
							variant={BookingNotFoundVariantEnum.SPECIALTY}
						/>
					</Stack>

					<PendingDialog
						open={
							checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_DUPLICITY ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_DISTRICTS ||
							checkBookingModal === CheckBookingModalEnum.VERIFYING_REQUEST_SCHEDULE
						}
						text={loadingText}
					/>

					{scheduledBooking ? (
						<TelehealthDuplicityDialog
							open={checkBookingModal === CheckBookingModalEnum.DUPLICITY_STATE}
							setOpen={() => {
								resetBookingDataSpecialties();
								setCheckBookingModal('none');
							}}
							scheduledBooking={scheduledBooking}
						/>
					) : null}

					<ScriptModal
						open={checkBookingModal === CheckBookingModalEnum.SCRIPT_STATE}
						setOpen={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
						handleNext={handleChooseScriptSuggestion}
					/>

					<TherapyAlert
						flowType={FlowEnum.TELEHEALTH}
						alertType="needAuth"
						open={checkBookingModal === CheckBookingModalEnum.NEED_AUTH}
						setOpen={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
					/>

					<AlertModal
						open={checkBookingModal === CheckBookingModalEnum.ERROR}
						onClose={() => {
							resetBookingDataSpecialties();
							setCheckBookingModal('none');
						}}
						description={serverError}
						title={serverErrorTitle}
						headerTitle={unexpectedError}
						buttonLabel={tryAgain}
						variant="info"
					/>
				</Fragment>
			) : null}
		</Fragment>
	);
}
